// ═══════════════════════════════════════════════════════════════════════════════
//  src/interview/InterviewRunner.jsx
//  INTERVIEW ROOM — la sala
//
//  Máquina de estados de un turno:
//     pregunta → (asistido: respuesta modelo) → grabando → veredicto → siguiente
//
//  ── LOS DOS MODOS ───────────────────────────────────────────────────────────
//  ASISTIDO  El teleprompter muestra la respuesta perfecta con la fonética
//            encima de las palabras duras. El alumno la lee en voz alta y el
//            servidor compara palabra por palabra con difflib. Sin IA en la
//            calificación: es aritmética, es exacta y no cuesta un token.
//  LIBRE     No hay texto. El alumno contesta como pueda y la IA lo juzga como
//            reclutador — primero si respondió la pregunta, y sólo al final el
//            idioma.
//
//  ── EL RECONOCIMIENTO DE VOZ ────────────────────────────────────────────────
//  Corre en el navegador (`browserSpeech`): 0 ms, 0 costo, y el audio nunca sale
//  del dispositivo. El límite conocido es que normaliza el acento, así que aquí
//  se mide lo que sí se puede medir — palabras dichas y ritmo — y la pantalla
//  no promete una nota de pronunciación que el instrumento no puede dar.
// ═══════════════════════════════════════════════════════════════════════════════

import { useCallback, useEffect, useRef, useState } from 'react';

import { speak, cancelSpeech, isSpeechAvailable, createRecognition, isRecognitionAvailable }
  from '../utils/browserSpeech';
import { nextQuestion, getCoachAnswer, submitAnswer, finishInterview } from './interviewApi';
import PhoneticText, { ReadingBooth } from './PhoneticText';

const PHASE_COLOR = {
  // Leen del tema. Antes eran hexadecimales fijos de modo oscuro, duplicados
  // en dos archivos: en claro habrian pintado neones sobre gris.
  icebreaker: 'var(--iv-ph-icebreaker)',
  background: 'var(--iv-ph-background)',
  technical: 'var(--iv-ph-technical)',
  behavioral: 'var(--iv-ph-behavioral)',
  closing: 'var(--iv-ph-closing)',
};

// Un emoji por fase. No es adorno: acompaña al color del arco y le da cara a
// cada tramo, que es lo que hace que la sala se sienta viva y no un formulario.
const PHASE_EMOJI = {
  icebreaker: '👋',
  background: '📄',
  technical: '⚙️',
  behavioral: '🧭',
  closing: '🤝',
};

const MAX_ANSWER_MS = 120000;   // tope de seguridad del micrófono

export default function InterviewRunner({ session, onFinished, onExit }) {
  const [turn, setTurn] = useState(null);
  const [ideal, setIdeal] = useState(null);
  // En que frase va el alumno. Vive aqui porque la lista (antes de grabar) y
  // la cabina (mientras graba) tienen que compartirlo: si se reiniciara al
  // pasar de una a otra, empezaria a leer desde arriba con el micro abierto.
  const [at, setAt] = useState(0);
  const [phase, setPhase] = useState('loading');  // loading|question|prompter|recording|grading|verdict|error
  const [transcript, setTranscript] = useState('');
  const [verdict, setVerdict] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const [error, setError] = useState(null);

  const recRef = useRef(null);
  const tickRef = useRef(null);
  const startedAt = useRef(0);
  const finalText = useRef('');
  const sent = useRef(false);

  const assisted = session.mode === 'assisted';
  const canHear = isRecognitionAvailable();
  const canSpeak = isSpeechAvailable();

  const cleanup = useCallback(() => {
    cancelSpeech();
    try { recRef.current?.stop(); } catch { /* ya detenido */ }
    recRef.current = null;
    clearInterval(tickRef.current);
  }, []);

  useEffect(() => cleanup, [cleanup]);

  /* ── Traer la siguiente pregunta ───────────────────────────────────────── */
  const loadQuestion = useCallback(async () => {
    setPhase('loading');
    setIdeal(null);
    setAt(0);
    setTranscript('');
    setVerdict(null);
    setElapsed(0);
    finalText.current = '';
    sent.current = false;

    try {
      const q = await nextQuestion(session.session_id);
      setTurn(q);
      setPhase('question');
      if (canSpeak) speak(q.question_en, { rate: 0.98 });
    } catch (e) {
      setError(e.message);
      setPhase('error');
    }
  }, [session.session_id, canSpeak]);

  useEffect(() => { loadQuestion(); }, [loadQuestion]);

  /* ── Modo asistido: pedir la respuesta modelo ──────────────────────────── */
  const loadIdeal = async () => {
    setPhase('loading');
    try {
      const data = await getCoachAnswer(session.session_id, turn.turn_id);
      setIdeal(data);
      setPhase('prompter');
    } catch (e) {
      setError(e.message);
      setPhase('error');
    }
  };

  /* ── Grabar ────────────────────────────────────────────────────────────── */
  const record = () => {
    if (!canHear) {
      setError('Este modo necesita reconocimiento de voz. Usa Chrome o Edge.');
      setPhase('error');
      return;
    }

    cancelSpeech();
    setTranscript('');
    finalText.current = '';
    setElapsed(0);
    setPhase('recording');
    startedAt.current = performance.now();

    const rec = createRecognition({
      onPartial: (t) => { finalText.current = t; setTranscript(t); },
      onFinal: (t) => { finalText.current = t || finalText.current; },
      onError: () => { /* se envía lo que haya */ },
    });
    recRef.current = rec;
    rec?.start();

    tickRef.current = setInterval(() => {
      const s = Math.round((performance.now() - startedAt.current) / 1000);
      setElapsed(s);
      if (s * 1000 >= MAX_ANSWER_MS) stop();
    }, 1000);
  };

  const stop = useCallback(async () => {
    if (sent.current) return;
    sent.current = true;

    clearInterval(tickRef.current);
    try { recRef.current?.stop(); } catch { /* ya detenido */ }

    const seconds = (performance.now() - startedAt.current) / 1000;
    setPhase('grading');

    try {
      const res = await submitAnswer({
        sessionId: session.session_id,
        turnId: turn.turn_id,
        transcript: finalText.current,
        seconds,
      });
      setVerdict(res);
      setPhase('verdict');
    } catch (e) {
      setError(e.message);
      setPhase('error');
    }
  }, [session.session_id, turn]);

  /* ── Avanzar ───────────────────────────────────────────────────────────── */
  const advance = async () => {
    cleanup();
    if (turn && turn.index >= turn.total) {
      setPhase('loading');
      try {
        onFinished(await finishInterview(session.session_id));
      } catch (e) {
        setError(e.message);
        setPhase('error');
      }
      return;
    }
    loadQuestion();
  };

  const quit = async () => {
    cleanup();
    // Aunque se salga a media entrevista, el reporte se cierra con lo hecho:
    // abandonar sin dejar rastro es perder el trabajo que ya hizo.
    try { onFinished(await finishInterview(session.session_id)); } catch { onExit(); }
  };

  /* ── Render ────────────────────────────────────────────────────────────── */
  const phaseId = turn?.phase?.id || 'icebreaker';
  const color = PHASE_COLOR[phaseId] || 'var(--iv-ac)';

  // Cabina: grabando y con guion que leer. Mientras dura, la pantalla se
  // reduce a lo imprescindible — la pregunta larga, la lista completa y los
  // controles sueltos obligaban a subir y bajar con el microfono abierto.
  const inBooth = phase === 'recording' && assisted && !!ideal;

  if (phase === 'error') {
    return (
      <div className="iv-screen">
        <div className="iv-fail">{error}</div>
        <div className="iv-actions">
          <button type="button" className="iv-btn" onClick={loadQuestion}>Reintentar</button>
          <button type="button" className="iv-btn iv-btn-ghost" onClick={quit}>Cerrar la entrevista</button>
        </div>
      </div>
    );
  }

  const score = verdict?.score;
  const tone = score === null || score === undefined ? 'iv-mid'
    : score >= 75 ? 'iv-ok' : score >= 45 ? 'iv-mid' : 'iv-bad';

  return (
    <div className={`iv-screen iv-ph-${phaseId}`}>
      <div className="iv-hud">
        <button type="button" className="iv-quit" onClick={quit}>← Terminar</button>
        {turn && <span className="iv-badge">{turn.phase.label}</span>}
        <span className="iv-spacer" />
        <span className="iv-badge">{assisted ? 'Asistido' : 'Libre'}</span>
        {turn && <span className="iv-count">{turn.index}/{turn.total}</span>}
      </div>

      {turn && (
        <>
          <div className="iv-track">
            {Array.from({ length: turn.total }, (_, k) => (
              <i
                key={k}
                style={{
                  flex: 1,
                  background: k < turn.index ? color : 'transparent',
                  opacity: k < turn.index ? 1 : 0,
                  marginRight: k === turn.total - 1 ? 0 : 2,
                }}
              />
            ))}
          </div>
          <div className="iv-meta">
            <span>{turn.phase.label}</span>
            <span>{session.role_label}</span>
          </div>
        </>
      )}

      {phase === 'loading' && <div className="iv-spin" />}

      {turn && phase !== 'loading' && !inBooth && (
        <div className="iv-card">
          {/* Alguien pregunta, y se ve. Antes era una etiqueta suelta sobre un
              párrafo: parecía un formulario, no una entrevista. */}
          <div className="iv-asker">
            <span className="iv-asker-face" aria-hidden="true">
              {PHASE_EMOJI[phaseId] || '👔'}
            </span>
            <div>
              <span className="iv-lbl">El entrevistador pregunta</span>
              <span className="iv-asker-phase">{turn.phase.label}</span>
            </div>
          </div>

          <p className="iv-q">{turn.question_en}</p>

          {turn.question_es && (
            <p className="iv-q-es">
              <span className="iv-q-es-tag" aria-hidden="true">ES</span>
              {turn.question_es}
            </p>
          )}

          {canSpeak && phase === 'question' && (
            <button type="button" className="iv-speak" onClick={() => speak(turn.question_en, { rate: 0.98 })}>
              🔊 Escuchar otra vez
            </button>
          )}
        </div>
      )}

      {/* ── Modo asistido: el teleprompter ── */}
      {ideal && phase === 'prompter' && (
        <div className="iv-prompter">
          <div className="iv-prompter-head">
            <span className="iv-lbl">🎙️ Léelo en voz alta</span>
            <span className="iv-prompter-hint">Toca cada línea al terminarla</span>
          </div>

          <PhoneticText text={ideal.ideal_en} at={at} onAt={setAt} />

          {/* El español ya no cuelga en gris debajo del inglés: es una sección
              aparte que se abre sólo si hace falta. Mezclarlos empujaba al
              alumno a leer traduciendo, que es justo lo contrario del
              ejercicio. */}
          {ideal.ideal_es && (
            <details className="iv-es">
              <summary className="iv-es-sum">🇪🇸 ¿Qué estoy diciendo?</summary>
              <p className="iv-es-body">{ideal.ideal_es}</p>
            </details>
          )}
        </div>
      )}

      {/* ── CABINA: guion, escucha y controles en una sola pantalla ── */}
      {inBooth && (
        <ReadingBooth
          text={ideal.ideal_en}
          at={at}
          onAt={setAt}
          elapsed={elapsed}
          transcript={transcript}
          onStop={stop}
          phaseLabel={turn?.phase?.label}
        />
      )}

      {/* Modo libre: no hay guion que leer, sólo reloj y lo que se capta. */}
      {phase === 'recording' && !inBooth && (
        <>
          <div className="iv-live">
            <span className="iv-dot" aria-hidden="true" />
            <span className="iv-num">{Math.floor(elapsed / 60)}:{String(elapsed % 60).padStart(2, '0')}</span>
            <span className="iv-wave" aria-hidden="true">
              {[0, 1, 2, 3, 4].map(b => <i key={b} />)}
            </span>
          </div>
          <div className="iv-transcript">{transcript}</div>
        </>
      )}

      {phase === 'grading' && (
        <>
          <div className="iv-spin" />
          <p className="iv-lbl" style={{ textAlign: 'center' }}>
            {assisted ? 'Comparando tu lectura…' : 'El reclutador está evaluando…'}
          </p>
        </>
      )}

      {phase === 'verdict' && verdict && (
        <div className={`iv-verdict ${tone}`}>
          {score !== null && score !== undefined
            ? <p className="iv-score iv-num">{Math.round(score)}<small>/100</small></p>
            : <p className="iv-lbl">Sin calificar</p>}

          <p className="iv-said">{verdict.verdict_es}</p>

          {verdict.errors?.length > 0 && (
            <ul className="iv-list">
              {verdict.errors.map((e) => (
                <li key={e.expected} className="iv-minus">
                  Dijiste <s>{e.heard}</s> donde iba <b>{e.expected}</b>
                </li>
              ))}
            </ul>
          )}

          {verdict.strengths?.length > 0 && (
            <ul className="iv-list">
              {verdict.strengths.map((s) => <li key={s} className="iv-plus">{s}</li>)}
            </ul>
          )}
          {verdict.improve?.length > 0 && (
            <ul className="iv-list">
              {verdict.improve.map((s) => <li key={s} className="iv-minus">{s}</li>)}
            </ul>
          )}

          <div className="iv-stats">
            {verdict.wpm > 0 && (
              <div><b className="iv-num">{Math.round(verdict.wpm)}</b><span>palabras/min</span></div>
            )}
            {verdict.total > 0 && (
              <div><b className="iv-num">{verdict.matched}/{verdict.total}</b><span>palabras</span></div>
            )}
            <div><b className="iv-num">{turn.index}/{turn.total}</b><span>pregunta</span></div>
          </div>
        </div>
      )}

      <div className="iv-actions">
        {phase === 'question' && (
          assisted
            ? <button type="button" className="iv-btn" onClick={loadIdeal}>Ver la respuesta modelo →</button>
            : <button type="button" className="iv-btn" onClick={record}>🎤 Responder</button>
        )}
        {phase === 'prompter' && (
          <button type="button" className="iv-btn" onClick={record}>🎤 Leerlo en voz alta</button>
        )}
        {phase === 'recording' && !inBooth && (
          <button type="button" className="iv-btn iv-btn-rec" onClick={stop}>■ Terminé</button>
        )}
        {phase === 'verdict' && (
          <button type="button" className="iv-btn" onClick={advance}>
            {turn.index >= turn.total ? 'Ver mi reporte →' : 'Siguiente pregunta →'}
          </button>
        )}
      </div>
    </div>
  );
}
